import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import Drawer from '@atlaskit/drawer';
import ArrowLeft from '@atlaskit/icon/core/migration/arrow-left';
import SuccessIcon from '@atlaskit/icon/core/migration/success--check-circle';
import { IntlMessagesProvider } from '@atlaskit/intl-messages-provider';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import Portal from '@atlaskit/portal';
import { G300 } from '@atlaskit/theme/colors';
import { layers } from '@atlaskit/theme/constants';
import { fetchMessagesForLocale } from '../../common/utils/fetch-messages-for-locale';
import i18nEN from '../../i18n/en';
import messages from '../../messages';
import { FlagEventType, isFlagEventTypeValue } from '../../types';
var iframeStyles = css({
  border: 0
});
var sidebarDivStyles = css({
  position: 'absolute',
  margin: "var(--ds-space-200, 16px)".concat(" 0 0 ", "var(--ds-space-200, 16px)"),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});
var sidebarButtonStyles = css({
  padding: 0,
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  background: 0,
  borderRadius: '50%',
  lineHeight: 1
});
var ANALYTICS_CHANNEL = 'atlas';
var GiveKudosLauncher = function GiveKudosLauncher(props) {
  var _props$recipient;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isCloseConfirmModalOpen = _useState2[0],
    setIsCloseConfirmModalOpen = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isDirty = _useState4[0],
    setIsDirty = _useState4[1];
  var iframeEl = useRef(null);
  var messageListenerEventHandler = useRef(function (e) {});
  var unloadEventHandler = useRef(function (e) {});
  var intl = useIntl();
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var addFlag = props.addFlag,
    teamCentralBaseUrl = props.teamCentralBaseUrl,
    analyticsSource = props.analyticsSource,
    onClose = props.onClose,
    testId = props.testId;
  var shouldBlockTransition = useCallback(function (e) {
    e.preventDefault();
    e.returnValue = intl.formatMessage(messages.unsavedKudosWarning);
  }, [intl]);
  var sendAnalytic = useCallback(function (action, options) {
    var analyticsEvent = createAnalyticsEvent({
      action: action,
      actionSubject: 'createKudos',
      attributes: _objectSpread(_objectSpread({}, options), {}, {
        analyticsSource: analyticsSource
      })
    });
    analyticsEvent.fire(ANALYTICS_CHANNEL);
  }, [analyticsSource, createAnalyticsEvent]);
  var closeDrawer = useCallback(function () {
    setIsDirty(false);
    setIsCloseConfirmModalOpen(false);
    onClose();
  }, [onClose]);
  var closeWarningModal = function closeWarningModal() {
    setIsCloseConfirmModalOpen(false);
  };
  var createFlagWithJsonStringifiedInput = useCallback(function (flagEvent) {
    var handleCreateOrFail = function handleCreateOrFail(addFlagConfig) {
      closeDrawer();
      sendAnalytic('created', {});
      if (flagEvent.kudosUuid || flagEvent.jiraKudosUrl) {
        addFlag && addFlag(addFlagConfig);
      }
    };
    switch (flagEvent.eventType) {
      case FlagEventType.KUDOS_CREATED:
        handleCreateOrFail({
          title: jsx(FormattedMessage, messages.kudosCreatedFlag),
          id: "kudosCreatedFlag-".concat(flagEvent.kudosUuid),
          description: jsx(FormattedMessage, _extends({}, messages.kudosCreatedDescriptionFlag, {
            values: {
              a: function a(s) {
                return jsx("a", {
                  href: "".concat(teamCentralBaseUrl, "/people/kudos/").concat(flagEvent.kudosUuid)
                }, s);
              }
            }
          })),
          icon: jsx(SuccessIcon, {
            spacing: "spacious",
            label: intl.formatMessage(messages.successIconLabel),
            color: "var(--ds-icon-success, ".concat(G300, ")")
          })
        });
        break;
      case FlagEventType.KUDOS_FAILED:
        handleCreateOrFail({
          title: jsx(FormattedMessage, messages.kudosCreationFailedFlag),
          id: "jiraKudosCreationFailedFlag-".concat(flagEvent.kudosUuid),
          description: jsx(FormattedMessage, messages.kudosCreationFailedDescriptionFlag)
        });
        break;
      case FlagEventType.JIRA_KUDOS_CREATED:
        handleCreateOrFail({
          title: jsx(FormattedMessage, messages.JiraKudosCreatedFlag),
          id: "kudosCreatedFlag-".concat(flagEvent.kudosUuid),
          description: jsx(FormattedMessage, messages.JiraKudosCreatedDescriptionFlag),
          icon: jsx(SuccessIcon, {
            spacing: "spacious",
            label: intl.formatMessage(messages.successIconLabel),
            color: "var(--ds-icon-success, ".concat(G300, ")")
          }),
          actions: [{
            content: 'Track gift request',
            href: flagEvent.jiraKudosUrl
          }, {
            content: 'View kudos',
            href: "".concat(teamCentralBaseUrl, "/people/kudos/").concat(flagEvent.kudosUuid)
          }]
        });
        break;
      case FlagEventType.JIRA_KUDOS_FAILED:
        handleCreateOrFail({
          title: jsx(FormattedMessage, messages.JiraKudosCreationFailedFlag),
          id: "jiraKudosCreationFailedFlag-".concat(flagEvent.kudosUuid),
          description: jsx(FormattedMessage, _extends({}, messages.JiraKudosCreationFailedDescriptionFlag, {
            values: {
              a: function a(s) {
                return jsx("a", {
                  href: flagEvent.jiraKudosFormUrl
                }, s);
              }
            }
          })),
          type: 'warning',
          actions: [{
            content: 'Visit go/kudos',
            href: flagEvent.jiraKudosFormUrl
          }, {
            content: 'View kudos',
            href: "".concat(teamCentralBaseUrl, "/people/kudos/").concat(flagEvent.kudosUuid)
          }]
        });
        break;
      case FlagEventType.DIRTY:
        setIsDirty(true);
        break;
      case FlagEventType.CLOSE:
        closeDrawer();
        break;
      default:
        // Not a known FlagEventType
        return;
    }
  }, [addFlag, closeDrawer, sendAnalytic, teamCentralBaseUrl, intl]);
  var messageListener = useCallback(function (event) {
    if (!props.isOpen) {
      return;
    }
    if (String(event.data).startsWith('kudos-created-') || event.data === 'dirty' || event.data === 'close') {
      if (String(event.data).startsWith('kudos-created-')) {
        var uuid = String(event.data).replace(/^(kudos-created-)/, '');
        closeDrawer();
        sendAnalytic('created', {});
        addFlag && addFlag({
          title: jsx(FormattedMessage, messages.kudosCreatedFlag),
          id: "kudosCreatedFlag-".concat(uuid),
          description: jsx(FormattedMessage, _extends({}, messages.kudosCreatedDescriptionFlag, {
            values: {
              a: function a(s) {
                return jsx("a", {
                  href: "".concat(teamCentralBaseUrl, "/people/kudos/").concat(uuid)
                }, s);
              }
            }
          })),
          icon: jsx(SuccessIcon, {
            spacing: "spacious",
            label: intl.formatMessage(messages.successIconLabel),
            color: "var(--ds-icon-success, ".concat(G300, ")")
          })
        });
      } else if (event.data === 'dirty') {
        setIsDirty(true);
      } else if (event.data === 'close') {
        closeDrawer();
      }
    } else {
      try {
        var eventData = JSON.parse(event.data);
        if (eventData.eventType && isFlagEventTypeValue(eventData.eventType)) {
          createFlagWithJsonStringifiedInput(eventData);
        }
      } catch (e) {
        // Swallow any errors
      }
    }
  }, [props.isOpen, closeDrawer, sendAnalytic, addFlag, teamCentralBaseUrl, createFlagWithJsonStringifiedInput, intl]);
  useEffect(function () {
    window.removeEventListener('message', messageListenerEventHandler.current);
    messageListenerEventHandler.current = messageListener;
    window.addEventListener('message', messageListenerEventHandler.current, false);
    return function () {
      window.removeEventListener('message', messageListenerEventHandler.current);
    };
  }, [messageListener]);
  useEffect(function () {
    window.removeEventListener('beforeunload', unloadEventHandler.current);
    if (isDirty) {
      unloadEventHandler.current = shouldBlockTransition;
      window.addEventListener('beforeunload', unloadEventHandler.current, false);
    }
    return function () {
      window.removeEventListener('beforeunload', unloadEventHandler.current);
    };
  }, [isDirty, shouldBlockTransition]);
  var sendCancelAnalytic = function sendCancelAnalytic() {
    sendAnalytic('cancelled', {});
  };
  var handleCloseDrawerClickedFunc = function handleCloseDrawerClickedFunc() {
    if (!isDirty) {
      sendCancelAnalytic();
      closeDrawer();
      return;
    }
    setIsCloseConfirmModalOpen(true);
  };
  var handleCloseDrawerClickedFuncRef = useRef(handleCloseDrawerClickedFunc);
  useEffect(function () {
    handleCloseDrawerClickedFuncRef.current = handleCloseDrawerClickedFunc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);
  var renderIframe = function renderIframe() {
    return jsx("iframe", {
      src: giveKudosUrl,
      ref: iframeEl,
      width: "100%",
      height: "100%",
      frameBorder: "0",
      allow: "camera;microphone",
      css: iframeStyles
    });
  };
  var handleCloseDrawerClicked = function handleCloseDrawerClicked() {
    handleCloseDrawerClickedFuncRef.current();
  };
  var renderSidebar = function renderSidebar() {
    return jsx("div", {
      css: sidebarDivStyles
    }, jsx(Button, {
      onClick: handleCloseDrawerClicked,
      css: sidebarButtonStyles
    }, jsx(ArrowLeft, {
      color: "currentColor",
      spacing: "spacious",
      label: intl.formatMessage(messages.closeDrawerButtonLabel)
    })));
  };
  var renderDrawer = useMemo(function () {
    if (props.isOpen) {
      sendAnalytic('opened', {});
    }
    return jsx(Drawer, {
      width: "full",
      isOpen: props.isOpen,
      zIndex: layers.modal(),
      onClose: handleCloseDrawerClicked
      // eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
      ,
      overrides: {
        Content: {
          component: renderIframe
        },
        Sidebar: {
          component: renderSidebar
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(_props$recipient = props.recipient) === null || _props$recipient === void 0 ? void 0 : _props$recipient.recipientId, props.isOpen]);
  var recipientParam = props.recipient ? "&type=".concat(props.recipient.type, "&recipientId=").concat(props.recipient.recipientId) : '';
  var giveKudosUrl = "".concat(props.teamCentralBaseUrl, "/give-kudos?cloudId=").concat(props.cloudId).concat(recipientParam, "&unsavedMessage=").concat(intl.formatMessage(messages.unsavedKudosWarning));
  return jsx(Portal, {
    zIndex: layers.modal()
  }, jsx("div", {
    "data-testid": testId
  }, jsx(ModalTransition, null, isCloseConfirmModalOpen && jsx(Modal, {
    onClose: closeWarningModal,
    width: "small"
  }, jsx(ModalHeader, null, jsx(ModalTitle, null, jsx(FormattedMessage, messages.confirmCloseTitle))), jsx(ModalBody, null, jsx(FormattedMessage, messages.unsavedKudosWarning)), jsx(ModalFooter, null, jsx(Button, {
    appearance: "subtle",
    onClick: closeWarningModal,
    autoFocus: true
  }, jsx(FormattedMessage, messages.unsavedKudosWarningCancelButton)), jsx(Button, {
    appearance: "primary",
    onClick: function onClick() {
      sendCancelAnalytic();
      closeDrawer();
    }
  }, jsx(FormattedMessage, messages.unsavedKudosWarningCloseButton))))), renderDrawer));
};
var ComposedGiveKudosLauncher = function ComposedGiveKudosLauncher(props) {
  return jsx(IntlMessagesProvider, {
    loaderFn: fetchMessagesForLocale,
    defaultMessages: i18nEN
  }, jsx(GiveKudosLauncher, props));
};
export default ComposedGiveKudosLauncher;